/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import Framework, { BaseService } from '@greenville/framework';
import * as constants from '../../../../common/constants';
import utils from '../../../../common/utils';
import handleEvalReportError, { resolveServerErrorMessage } from './evalErrors';
import notifyRubrics from './rubricsNotifications';
import { buildRubricsTextPath } from './rubricsApiPaths';

/**
 * PUT /rubrics/:rubricId/rubrictext — the only write that touches the definition.
 *
 * It takes name, types and rubric text together, and appends a row at the next rubricVersion rather
 * than updating in place, carrying the existing schedule and sampling forward. So saving here never
 * disturbs a schedule, but it does always produce a new version.
 */
export default class RubricsUpdateService extends BaseService {
  constructor() {
    super(constants.UPDATE_RUBRICS_RUBRIC, 'RubricsUpdateStatus');
  }

  async handleEvent(eventName, event) {
    const { graphId, rubricId, tenantId, requestBody } = event;
    const headers = utils.buildHeadersWithTenantId({ tenantId });
    const url = buildRubricsTextPath({ rubricId, graphId });
    const { isStackB } = utils.getStackBParams();

    if (isStackB) {
      const enhancedHeaders = await utils.resolveStackBHeaders(headers);
      return this.getClient('aiadminstackb').put(url, requestBody, { headers: enhancedHeaders });
    }
    return this.getClient('aiadmin').put(url, requestBody, { headers });
  }

  handleResponse(response) {
    const { data } = response;
    const store = Framework.getStoreRegistry().getStore('rubricsData');

    if (data?.success) {
      notifyRubrics({ type: 'success', langMessage: 'rubrics.UPDATE_SUCCESS' });
      store.completeUpdate();
      return;
    }

    notifyRubrics({ type: 'error', message: resolveServerErrorMessage(data), langMessage: 'rubrics.UPDATE_ERROR' });
    store.failUpdate();
  }

  handleError(error) {
    Framework.getStoreRegistry().getStore('rubricsData').failUpdate();
    handleEvalReportError(error, 'rubrics.UPDATE_ERROR');
  }
}
